export type Role = 'foster' | 'admin' | 'super_admin';
export type Condition = 'new' | 'used';

export interface Me {
  id: string;
  email: string;
  display_name: string | null;
  role: Role;
  status: string;
  preferred_location_id: string | null;
}

export interface Settings {
  monthly_reservation_limit: number;
  unpickup_notify_days: number;
  unpickup_notify_targets: string;
}

export interface Location {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  short_code: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface Category {
  id: string;
  name: string;
  sort_order: number;
}

export interface ReservationTag {
  id: string;
  name: string;
  color: string | null;
  sort_order: number;
}

export interface Item {
  id: string;
  description: string;
  category_id: string | null;
  category_name?: string | null;
  condition: Condition;
  quantity_total: number;
  quantity_available: number;
  estimated_value: string | number | null;
  photo_ref: string | null;   // computed display URL (from the linked media row)
  media_id: string | null;    // FK into the media library
  notes: string | null;
  location_id: string | null;
  location_name?: string | null;
  location_short_code?: string | null;
  status: 'available' | 'closed';
  donated_at: string | null;
  created_at: string;
  tags?: { id: string; name: string; color: string | null }[];
}

/** Semicolon-joined tag names for a table cell — used for sort/filter/CSV. */
export function tagNames(tags?: { name: string }[] | null): string {
  return (tags || []).map((t) => t.name).join('; ');
}

export interface Reservation {
  id: string;
  item_id: string;
  user_id: string;
  quantity: number;
  location_id: string | null;
  status: 'reserved' | 'ready' | 'picked_up' | 'cancelled' | 'released';
  reserved_at: string;
  picked_up_at: string | null;
  released_at: string | null;
  item_description?: string;
  photo_ref?: string | null;
  category_name?: string | null;
  location_name?: string | null;
  location_address?: string | null;
  location_phone?: string | null;
  foster_name?: string | null;
  foster_email?: string | null;
  tags?: { id: string; name: string; color: string | null }[];
}

// Human label for a reservation status. 'ready' reads as the full phrase; the
// rest just swap the underscore for a space.
export function reservationStatusLabel(status: string): string {
  if (status === 'ready') return 'Ready to be picked up';
  return status.replace('_', ' ');
}

export interface Bootstrap {
  me: Me;
  settings: Settings;
  locations: Location[];
  categories: Category[];
  reservation_tags: ReservationTag[];
}

export interface AppUser {
  id: string;
  email: string;
  display_name: string | null;
  role: Role;
  status: string;
  preferred_location_id: string | null;
  created_at: string;
  monthly_reserved?: number;
  monthly_picked_up?: number;
}

export interface Media {
  id: string;
  url: string;
  filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  created_at: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: Role;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

// ─── Person history (People-page detail popup) ──────────────────────────────

/** One sign-in session from the platform auth tier (user_sessions). */
export interface AuthSession {
  familyId: string;
  signedInAt: string;
  lastActiveAt: string;
  signedOutAt: string | null;
  expiresAt: string;
  userAgent: string | null;
  ip: string | null;
  reuseDetected: boolean;
  active: boolean;
}

/** One app-level event on this person's timeline. */
export interface UserEvent {
  id: string;
  event_type: string;
  summary: string;
  details: Record<string, unknown> | null;
  created_at: string;
  actor_name: string | null;
  actor_email: string | null;
  by_self: boolean;
}

export interface UserHistory {
  user: {
    id: string;
    email: string;
    display_name: string | null;
    role: Role;
    status: string;
    created_at: string;
  };
  sessions: AuthSession[];
  events: UserEvent[];
}
